import { Alert, Text, View } from 'react-native';
import { CartaoApoio } from '../components/Recolhivel';
import type { Colecao, ModDef, PainelProps } from './tipos';
import { dataBr, din, junta, num, op } from './util';
import { novoReg } from '../store/modulos';
import { compartilharTexto } from '../lib/anexos';
import { semBloqueio } from '../auth/AuthContext';
import { C } from '../components/theme';
import { today, horaTela } from '../lib/date';
import { N_, t } from '../lib/i18n';

const COMODOS = [N_('Sala'), N_('Cozinha'), N_('Quarto'), N_('Quarto das crianças'), N_('Banheiro'), N_('Escritório'), N_('Lavanderia'), N_('Garagem'), N_('Área externa'), N_('Outro')];
const SERVICOS = [N_('Luz'), N_('Água'), N_('Gás'), N_('Internet'), N_('Telefone'), N_('TV por assinatura'), N_('Condomínio'), N_('Endereço nos bancos e cartões'),
  N_('Endereço nos Correios e entregas'), N_('Endereço no trabalho e na escola'), N_('Seguro da casa'), N_('Outro')];

/** Texto da etiqueta da caixa para imprimir ou mandar. */
const etiqueta = (c: Record<string, any>) => [
  t('CAIXA {n}', { n: c.numero || '?' }) + (c.fragil ? ' · ⚠️ ' + t('FRÁGIL') : ''),
  op(c.comodo).toUpperCase(), c.conteudo ? '\n' + c.conteudo : '',
].filter(Boolean).join('\n');

function PainelCaixas({ lista }: PainelProps) {
  if (!lista.length) return null;
  const por = lista.reduce<Record<string, number>>((m, r) => { const k = op(r.c.comodo) || t('Sem cômodo'); m[k] = (m[k] ?? 0) + 1; return m; }, {});
  const abertas = lista.filter(r => r.c.aberta).length;
  return (
    <CartaoApoio titulo={'📦 ' + t('{n} caixa(s) · {a} já aberta(s) na casa nova', { n: lista.length, a: abertas })} chave={'mudanca:caixas'} gap={6}>
      <Text style={{ color: C.sub, fontSize: 12 }}>{Object.entries(por).map(([k, v]) => `${k}: ${v}`).join(' · ')}</Text>
      {lista.some(r => r.c.fragil) && <Text style={{ color: C.warn, fontSize: 12 }}>⚠️ {t('{n} caixa(s) frágil(eis)', { n: lista.filter(r => r.c.fragil).length })}</Text>}
    </CartaoApoio>
  );
}

const mudanca: Colecao = {
  id: 'mudanca', nome: N_('Mudanças'), um: N_('Mudança'), icone: '🚚',
  campos: [
    { k: 'para', r: N_('Endereço novo'), t: 'endereco' },
    { k: 'de', r: N_('Endereço atual'), t: 'endereco' },
    { k: 'data', r: N_('Dia da mudança'), t: 'data', aviso: [7, 1] },
    { k: 'empresa', r: N_('Empresa de mudança'), t: 'texto' },
    { k: 'telefone', r: N_('Telefone da empresa'), t: 'telefone' },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro' },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => r.c.para || '', sub: (r, ctx) => junta(r.c.empresa, r.c.valor !== '' && r.c.valor != null && din(r.c.valor, ctx.moeda),
    t('{n} caixa(s)', { n: ctx.filhos(r.id, 'caixa').length }), t('{f}/{n} transferências feitas', { f: ctx.filhos(r.id, 'servico').filter(x => x.c.feito).length, n: ctx.filhos(r.id, 'servico').length })),
  data: 'data', anexos: true,
  agenda: r => ({ title: t('Mudança: {para}', { para: r.c.para || '' }), notes: junta(r.c.empresa, r.c.telefone) }),
  // a lista de transferências já vem pronta
  semear: (_, r) => SERVICOS.slice(0, -1).map(s => novoReg('servico', r.id, { tipo: s, feito: false })),
  vazio: N_('Crie a mudança e o app já monta a lista de transferências (luz, água, internet...). Depois cadastre as caixas por cômodo, as vistorias e o cronograma dos dias.'),
};

const caixa: Colecao = {
  id: 'caixa', check: 'aberta', pai: 'mudanca', nome: N_('Caixas'), um: N_('Caixa'), icone: r => (r.c.aberta ? '📭' : r.c.fragil ? '⚠️' : '📦'),
  campos: [
    { k: 'numero', r: N_('Número da caixa'), t: 'numero' },
    { k: 'comodo', r: N_('Cômodo'), t: 'opcao', op: COMODOS },
    { k: 'conteudo', r: N_('O que tem dentro'), t: 'longo' },
    { k: 'fragil', r: N_('Frágil'), t: 'sim' },
    { k: 'aberta', r: N_('Já aberta na casa nova'), t: 'sim' },
  ],
  titulo: r => junta(t('Caixa {n}', { n: r.c.numero || '?' }), op(r.c.comodo)), sub: r => String(r.c.conteudo || '').replace(/\s+/g, ' ').slice(0, 100),
  ordem: (a, b) => num(a.c.numero) - num(b.c.numero), feito: r => !!r.c.aberta, anexos: true,
  novo: (ctx, pai) => ({ numero: Math.max(0, ...ctx.regs.filter(r => r.col === 'caixa' && r.pai === pai?.id).map(r => num(r.c.numero))) + 1 }),
  filtros: COMODOS.slice(0, 8).map(c => [c, r => r.c.comodo === c] as [string, (r: any) => boolean]),
  acoes: r => [{ label: '🏷️ ' + t('Enviar ou imprimir a etiqueta'), onPress: () => semBloqueio(() => compartilharTexto(etiqueta(r.c), t('Caixa {n}', { n: r.c.numero }))).then(x => { if (x === 'copiado') Alert.alert(t('Texto copiado. Cole onde quiser enviar.')); }).catch(() => {}) }],
  painel: PainelCaixas,
  vazio: N_('Numere cada caixa e diga o cômodo e o conteúdo. Use a busca para achar onde está cada coisa.'),
};

const servico: Colecao = {
  id: 'servico', check: 'feito', pai: 'mudanca', nome: N_('Transferências'), um: N_('Transferência'), icone: r => (r.c.feito ? '✅' : '🔌'),
  campos: [
    { k: 'tipo', r: N_('Serviço'), t: 'opcao', op: SERVICOS },
    { k: 'empresa', r: N_('Empresa'), t: 'texto' },
    { k: 'protocolo', r: N_('Protocolo'), t: 'texto' },
    { k: 'data', r: N_('Data agendada'), t: 'data', aviso: [1] },
    { k: 'feito', r: N_('Feito'), t: 'sim' },
  ],
  titulo: r => op(r.c.tipo), sub: r => junta(r.c.empresa, r.c.protocolo && `#${r.c.protocolo}`), data: 'data', feito: r => !!r.c.feito,
  ordem: 'criado', agenda: r => ({ title: t('Transferir: {s}', { s: op(r.c.tipo) }) }),
  resumo: l => (l.length ? t('{f} de {n} feitas', { f: l.filter(r => r.c.feito).length, n: l.length }) : null),
};

const vistoria: Colecao = {
  id: 'vistoria', pai: 'mudanca', nome: N_('Vistorias'), um: N_('Vistoria'), icone: '🔍',
  campos: [
    { k: 'imovel', r: N_('Imóvel'), t: 'opcao', op: [N_('Saída (imóvel antigo)'), N_('Entrada (imóvel novo)')] },
    { k: 'data', r: N_('Data'), t: 'data', aviso: [1] },
    { k: 'hora', r: N_('Horário'), t: 'hora' },
    { k: 'responsavel', r: N_('Responsável (imobiliária, vistoriador)'), t: 'texto' },
    { k: 'notas', r: N_('O que foi visto'), t: 'longo' },
  ],
  titulo: r => op(r.c.imovel) || t('Vistoria'), sub: r => junta(horaTela(r.c.hora), r.c.responsavel), data: 'data', anexos: true,
  agenda: r => ({ time: r.c.hora || null, title: t('Vistoria: {i}', { i: op(r.c.imovel) }) }),
  vazio: N_('Fotografe cada cômodo na vistoria de saída e na de entrada. As fotos protegem você na hora de devolver o imóvel.'),
};

const dia: Colecao = {
  id: 'dia', check: 'feito', pai: 'mudanca', nome: N_('Cronograma'), um: N_('Etapa do cronograma'), icone: r => (r.c.feito ? '✅' : '🗓️'),
  campos: [
    { k: 'atividade', r: N_('Atividade'), t: 'texto', ph: N_('Ex.: Desmontar os móveis do quarto') },
    { k: 'data', r: N_('Dia'), t: 'data' },
    { k: 'hora', r: N_('Horário'), t: 'hora' },
    { k: 'quem', r: N_('Responsável'), t: 'texto' },
    { k: 'feito', r: N_('Feito'), t: 'sim' },
  ],
  titulo: r => r.c.atividade || '', sub: r => junta(dataBr(r.c.data), horaTela(r.c.hora), r.c.quem), data: 'data', feito: r => !!r.c.feito,
  ordem: (a, b) => `${a.c.data || '9'}${a.c.hora || '99'}`.localeCompare(`${b.c.data || '9'}${b.c.hora || '99'}`),
  novo: (_, pai) => ({ data: pai?.c.data || today() }),
  agenda: r => ({ time: r.c.hora || null }),
};

export const MUDANCA: ModDef = { id: 'mudanca', colecoes: [mudanca, caixa, servico, vistoria, dia] };
